import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { TourProvider } from "@reactour/tour";
import { TourPreferencesProvider } from "./components/ProductTour";
import ProtectedRoute from "./components/ProtectedRoute";
import AuthCallback from "./components/AuthCallback";
import MicrosoftCallback from "./components/MicrosoftCallback";
import ErrorBoundary from "./components/ErrorBoundary";
import PendingInvitationBanner from "./components/PendingInvitationBanner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import UploadPage from "./pages/UploadPage";
import CleaningPage from "./pages/CleaningPage";
import AIAssistantPage from "./pages/AIAssistantPage";
import FileSplitterPage from "./pages/FileSplitterPage";
import AccountPage from "./pages/AccountPage";
import AdminPage from "./pages/AdminPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import BlogManagerPage from "./pages/BlogManagerPage";
import UseCaseManagerPage from "./pages/UseCaseManagerPage";
import UseCasePage from "./pages/UseCasePage";
import TemplatesPage from "./pages/TemplatesPage";
import CRMDoctorPage from "./pages/CRMDoctorPage";
import PhoneFormatterPage from "./pages/PhoneFormatterPage";
import EmailFormatterPage from "./pages/EmailFormatterPage";
import NameSplitterPage from "./pages/NameSplitterPage";
import GenderDetectorPage from "./pages/GenderDetectorPage";
import EmailToNamePage from "./pages/EmailToNamePage";
import LeadGenDashboardPage from "./pages/LeadGenDashboardPage";
import DemoPage from "./pages/DemoPage";
import { BlogListPage, BlogPostPage } from "./pages/BlogPage";
import JoinTeamPage from "./pages/JoinTeamPage";
import UseCasesListPage from "./pages/UseCasesListPage";
import CompareHubSpotPage from "./pages/CompareHubSpotPage";
import CompareHubSpotPageEN from "./pages/CompareHubSpotPageEN";
import ROICalculatorPage from "./pages/ROICalculatorPage";

// Router wrapper to handle auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (Google OAuth callback)
  // This must be checked synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/microsoft/callback" element={<MicrosoftCallback />} />
      {/* Pricing/credits/payment removed - tool is free and unlimited */}
      <Route path="/pricing" element={<Navigate to="/dashboard" replace />} />
      <Route path="/pricing/credits" element={<Navigate to="/dashboard" replace />} />
      <Route path="/pricing/subscription" element={<Navigate to="/dashboard" replace />} />
      <Route path="/credits" element={<Navigate to="/dashboard" replace />} />
      <Route path="/payment-success" element={<Navigate to="/dashboard" replace />} />
      <Route path="/tools/split" element={<FileSplitterPage />} />
      <Route path="/tools/crm-doctor" element={<CRMDoctorPage />} />
      <Route path="/tools/csv-doctor" element={<CRMDoctorPage />} /> {/* Legacy route redirect */}
      <Route path="/tools/phone-formatter" element={<PhoneFormatterPage />} />
      <Route path="/tools/email-formatter" element={<EmailFormatterPage />} />
      <Route path="/tools/name-splitter" element={<NameSplitterPage />} />
      <Route path="/tools/gender-detector" element={<GenderDetectorPage />} />
      <Route path="/tools/email-to-name" element={<EmailToNamePage />} />
      <Route path="/tools/roi-calculator" element={<ROICalculatorPage />} />
      <Route path="/hubspot-phone-fixer" element={<PhoneFormatterPage />} />
      <Route path="/templates" element={<TemplatesPage />} />
      <Route path="/join-team" element={<JoinTeamPage />} />
      <Route path="/use-case/:slug" element={<UseCasePage />} />
      
      {/* Resources Routes */}
      <Route path="/resources/blog" element={<BlogListPage />} />
      <Route path="/resources/use-cases" element={<UseCasesListPage />} />
      <Route path="/compare/hubspot" element={<CompareHubSpotPage />} />
      <Route path="/compare/hubspot/en" element={<CompareHubSpotPageEN />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/blog/:pillarSlug/:slug" element={<BlogPostPage />} />
      
      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upload" 
        element={
          <ProtectedRoute>
            <UploadPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clean/:fileId" 
        element={
          <ProtectedRoute>
            <CleaningPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clean/:step/:fileId" 
        element={
          <ProtectedRoute>
            <CleaningPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/ai-assistant" 
        element={
          <ProtectedRoute>
            <AIAssistantPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/account" 
        element={
          <ProtectedRoute>
            <AccountPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/dashboard" 
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/blog-manager" 
        element={
          <ProtectedRoute>
            <BlogManagerPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/lead-gen" 
        element={
          <ProtectedRoute>
            <LeadGenDashboardPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin/use-case-manager" 
        element={
          <ProtectedRoute>
            <UseCaseManagerPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Public Pages */}
      <Route path="/features" element={<DemoPage />} />
      <Route path="/demo" element={<DemoPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/blog/:pillarSlug/:slug" element={<BlogPostPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
}

// Tour styling configuration
const tourStyles = {
  popover: (base) => ({
    ...base,
    '--reactour-accent': '#4f46e5',
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    padding: '20px',
    maxWidth: '400px',
  }),
  maskArea: (base) => ({ ...base, rx: 12 }),
  maskWrapper: (base) => ({ ...base, color: 'rgba(15, 23, 42, 0.75)' }),
  badge: (base) => ({ ...base, backgroundColor: '#4f46e5' }),
  controls: (base) => ({ ...base, marginTop: '16px' }),
  close: (base) => ({ ...base, right: '12px', top: '12px' }),
};

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <TourPreferencesProvider>
            <TourProvider
              steps={[]}
              styles={tourStyles}
              showBadge={true}
              showCloseButton={true}
              disableInteraction={false}
              scrollSmooth={true}
              padding={{ mask: 10, popover: [10, 15] }}
              onClickMask={() => {}}
            >
              <PendingInvitationBanner />
              <AppRouter />
              <Toaster position="top-right" richColors />
            </TourProvider>
          </TourPreferencesProvider>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
